import type { CustomUJSResponse } from './modal_form'

type Accomplishment = {
  id: number | null
  completed_at: string
  teaching_type: string
}

type Language = {
  id: number
  code: string
}

type QuizResult = {
  id: number | null
  score: number | null
  passed: boolean
}

type VideoWatchLog = {
  id: number | null
  watch_time_seconds: number
  orig_watch_time: number
  watched_percentage: number
  created_at: string | null
  updated_at: string | null
}

type Video = {
  id: number
  name: string
  duration: number
  language_id: number
}

type VideoData = {
  video: Video
  log: VideoWatchLog
}

type VideoWatchLogResponse = {
  video_watch_log: VideoWatchLog
  msg: string
}

type AccomplishmentResponse = {
  id: number
  msg: string
}

export const productResults = (
  materialPackageId: number,
  driverId: number,
  maximumScore: number,
  accomplishment: Accomplishment,
  quizResult: QuizResult,
  videoData: VideoData[],
  languages: Language[],
) => ({
  maximumScore,
  accomplishment,
  quizResult,
  videoData,
  languages,

  init(): void {
    this.videoData.forEach((data) => this.initVideoWatchLog(data))
  },

  get fullyWatched(): boolean {
    return this.totalPercentageWatched >= 98
  },

  get totalPercentageWatched(): number {
    const { languageId, watchTime } = this.totalWatchTime
    return this.watchedPercentage(this.totalVideoDuration(languageId), watchTime)
  },

  get totalWatchTime(): { languageId: number; watchTime: number } {
    const totals = new Map<number, number>()

    for (const data of this.videoData) {
      const { watch_time_seconds } = data.log
      const { language_id } = data.video

      totals.set(language_id, (totals.get(language_id) ?? 0) + watch_time_seconds)
    }

    const [languageId, watchTime] = [...totals.entries()].reduce((max, current) =>
      current[1] > max[1] ? current : max,
    )

    return { languageId, watchTime }
  },

  totalVideoDuration(languageId: number): number {
    return this.videoData
      .filter((d) => d.video.language_id === languageId)
      .reduce((acc, { video }) => acc + video.duration, 0)
  },

  dateTime(str: string): string {
    const locale = document.querySelector('html')?.getAttribute('lang') || 'fi'
    return new Date(str).toLocaleString(locale, { dateStyle: 'short', timeStyle: 'short' })
  },

  // Set up some attributes to keep track of changes
  initVideoWatchLog({ log, video }: VideoData): void {
    log.orig_watch_time = log.watch_time_seconds
    log.watched_percentage = this.watchedPercentage(video.duration, log.watch_time_seconds)
  },

  createAccomplishment(): void {
    const { id, msg } = (this.$event as CustomUJSResponse<AccomplishmentResponse>).detail[0]
    this.accomplishment = { id, teaching_type: 'previously_completed', completed_at: '' }
    window.flash('notice', msg)
  },

  removeAccomplishment(): void {
    this.accomplishment = { id: null, teaching_type: '', completed_at: '' }
    window.flash('notice', (this.$event as CustomUJSResponse<AccomplishmentResponse>).detail[0].msg)
  },

  removeQuizResult(): void {
    this.videoData.forEach((data) => {
      data.log = {
        id: null,
        watch_time_seconds: 0,
        orig_watch_time: 0,
        watched_percentage: 0,
        created_at: null,
        updated_at: null,
      }
    })

    Object.assign(this.quizResult, { id: null, score: null, passed: false })
    window.flash('notice', (this.$event as CustomUJSResponse<AccomplishmentResponse>).detail[0].msg)
  },

  async saveVideoWatchLog(log: VideoWatchLog, video: Video): Promise<void> {
    let url = '/video_watch_logs'
    if (log.id) url += `/${log.id}`

    const data = {
      id: log.id,
      material_package_id: materialPackageId,
      driver_id: driverId,
      watch_time_seconds: log.watch_time_seconds,
      video_id: video.id,
      language_id: video.language_id,
    }

    try {
      const res = await fetch(url, {
        method: log.id ? 'PATCH' : 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ video_watch_log: data }),
      })

      if (!res.ok) throw new Error()

      const { video_watch_log, msg } = (await res.json()) as VideoWatchLogResponse
      log.id = video_watch_log.id
      log.created_at = video_watch_log.created_at
      log.updated_at = video_watch_log.updated_at

      // Re-initialize result to make the created/updated values the new base
      this.initVideoWatchLog({ log, video })

      window.flash('notice', msg)
    } catch (error) {
      alert(window.I18n.unknown_error)
    }
  },

  removeVideoWatchLog(log: VideoWatchLog): void {
    Object.assign(log, {
      id: null,
      watch_time_seconds: 0,
      orig_watch_time: 0,
      watched_percentage: 0,
      created_at: null,
      updated_at: null,
    })

    window.flash('notice', (this.$event as CustomUJSResponse<VideoWatchLogResponse>).detail[0].msg)
  },

  secondsToString(seconds: number): string {
    const h = Math.floor(seconds / 3600)
      .toString()
      .padStart(2, '0')
    const m = Math.floor((seconds % 3600) / 60)
      .toString()
      .padStart(2, '0')
    const s = Math.floor(seconds % 60)
      .toString()
      .padStart(2, '0')

    return `${h}:${m}:${s}`
  },

  watchTime(duration: number, percentage: number | null): number {
    return percentage ? Math.floor((duration / 100.0) * percentage) : 0
  },

  watchedPercentage(duration: number, watchTime: number | null): number {
    const percentage = watchTime ? Math.round((watchTime / duration) * 100) : 0
    return Math.min(100, percentage)
  },
})
